import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';

import {
  CONVERSATION_SEARCH_MAX_LENGTH,
  listConversations,
  type ConversationView,
  type PagedConversations,
} from '@/features/conversations/conversations.api';
import { conversationsKeys } from '@/features/conversations/keys';

/** Page size for the conversation inbox. Matches the Hub default of 50. */
const PAGE_SIZE = 50;

interface UseConversationsArgs {
  /** `null` fetches the aggregate "所有账号" list across every account (no
   *  `account_id` filter sent). A UUID scopes the list to one account. */
  accountId: string | null;
  /** Flag-driven row filter (`view=unread_pin` | `view=cuci`); absent = the
   *  "All" tab (no filter, server four-zone ordering). Part of the queryKey
   *  so switching tabs is a real refetch. */
  view?: ConversationView;
  /** Search text, filtered SERVER-side via the backend's `q=` param. In the
   *  queryKey AND the request together (normalized below), so every search
   *  string is its own cache entry fetched with its own filter. */
  search?: string;
}

/**
 * Infinite list of conversations — either one platform account's inbox, or
 * the aggregate "所有账号" queue when `accountId` is `null`.
 *
 *   - `getNextPageParam` returns the next offset (or undefined when we've
 *     consumed `total`).
 *   - `staleTime: 15s` — consistent with the accounts query in M5-T3.
 *   - `refetchInterval: 30s` — poll until M5-T6 wires the WebSocket
 *     `conversation.updated` event; same TODO marker as `useAccounts`.
 *   - `ConversationColumn` always mounts `ConversationList` now (the all-accounts view removed
 *     the old "no account picked" placeholder), so this hook always fires
 *     once mounted — there is no `enabled` gate on `accountId` any more.
 */
export function useConversations(
  args: UseConversationsArgs,
): UseInfiniteQueryResult<{ pages: PagedConversations[]; pageParams: number[] }, Error> {
  const { accountId, view } = args;

  // Normalize the search ONCE, before it feeds the queryKey and the request:
  // trimmed, clamped to the backend's 64-char cap, and empty → undefined. The
  // key and the fetch can then never disagree (a key slot of `'  '` fetching
  // `q=` unfiltered would poison the cache), and clearing the input lands
  // back on the SAME cache entry as "never searched" instead of a cold
  // refetch. The clamp is the wire-level guard: the input's `maxLength`
  // already stops keystrokes, but an over-length value arriving any other
  // way would 422 the query and blank the whole list with an unretryable
  // error screen (see CONVERSATION_SEARCH_MAX_LENGTH).
  const search =
    args.search?.trim().slice(0, CONVERSATION_SEARCH_MAX_LENGTH) || undefined;

  // Same-phone grouping is only meaningful for the aggregate ("所有账号")
  // view, where one customer can appear once per account; a single-account
  // list can never have two rows for the same number, so we leave the default
  // recency ordering untouched there.
  const group = accountId === null ? 'phone' : undefined;

  return useInfiniteQuery<
    PagedConversations,
    Error,
    { pages: PagedConversations[]; pageParams: number[] },
    ReturnType<typeof conversationsKeys.list>,
    number
  >({
    queryKey: conversationsKeys.list({ accountId, view, group, search }),
    queryFn: ({ pageParam }) =>
      listConversations({
        accountId,
        view,
        group,
        search,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // `lastPage.total` is the unpaged count for the current filter; sum the
      // items rendered so far and compare. `undefined` tells react-query the
      // list is exhausted.
      const consumed = allPages.reduce((sum, p) => sum + p.items.length, 0);
      if (consumed >= lastPage.total) {
        return undefined;
      }
      return consumed;
    },
    staleTime: 15_000,
    // TODO(M5-T6): replace polling with WebSocket-driven `setQueryData`
    // updates from `conversation.updated` / `conversation.lock` events.
    refetchInterval: 30_000,
    retry: 1,
  });
}
